import { GenerateNameRequest, GenerateNameResponse } from '../../types/name';

// API Configuration Logging
console.log('=== API Configuration ===');
console.log('Environment:', import.meta.env.PROD ? 'production' : 'development');
console.log('Current hostname:', window.location.hostname);
console.log('import.meta.env.PROD:', import.meta.env.PROD);

// 确定是否为本地开发环境
const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// API Configuration
const API_CONFIG = {
  BASE_URL: import.meta.env.PROD 
    ? window.location.hostname.includes('chinese-name.net')
      ? window.location.origin
      : 'https://ai-chinese-name-43aw3jgrv-lickfengs-projects.vercel.app'
    : 'http://localhost:3000', // 开发环境使用Vite默认端口
  ENDPOINTS: {
    GENERATE_NAME: '/api/generate-name'
  },
  TIMEOUT: 60000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000
};

// Log API configuration for debugging
console.log('Is local dev:', isLocalDev);
console.log('Selected BASE_URL:', API_CONFIG.BASE_URL);
console.log('Full API URL will be:', `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GENERATE_NAME}`);

// Custom Error Class
class APIError extends Error {
  constructor(
    public message: string,
    public statusCode?: number,
    public type: 'network' | 'timeout' | 'validation' | 'server' = 'server',
    public details?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

// Configuration Constants
const CONFIG = {
  TIMEOUT: 32000,       // 32 seconds timeout (slightly above Cloudflare 30s limit)
  RETRY_COUNT: 1,       // Maximum retry attempts (reduced from 2)
  RETRY_DELAY: 1500,    // 1.5 seconds delay between retries
} as const;

// Create timeout Promise
const timeoutPromise = (ms: number) =>
  new Promise((_, reject) =>
    setTimeout(() => reject(new APIError('Request timed out', undefined, 'timeout')), ms)
  );

// Delay function
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Validate request parameters
const validateRequest = (request: GenerateNameRequest): void => {
  if (!request.firstName?.trim()) {
    throw new APIError('First name is required', 400, 'validation');
  }
  if (!request.surname?.trim()) {
    throw new APIError('Surname is required', 400, 'validation');
  }
};

// Get full URL
const getFullUrl = (path: string) => {
  return `${API_CONFIG.BASE_URL}${path}`;
};

// Make single request
const makeRequest = async (
  firstName: string,
  surname: string,
  gender: 'boy' | 'girl' | 'any' = 'any',
  signal: AbortSignal
): Promise<Response> => {
  // 开发环境下，确保使用正确的API路径
  const apiUrl = import.meta.env.DEV 
    ? API_CONFIG.ENDPOINTS.GENERATE_NAME // 开发环境使用相对路径，让Vite代理处理
    : getFullUrl(API_CONFIG.ENDPOINTS.GENERATE_NAME); // 生产环境使用完整路径
    
  console.log('Making request to:', apiUrl);
  
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ firstName, surname, gender }),
    signal
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.text();
    } catch {
      errorData = 'Failed to read error response';
    }

    // Special handling for 504 Gateway Timeout
    if (response.status === 504) {
      throw new APIError(
        'AI response timeout. The request may be too complex. Please try again or simplify your request.',
        504,
        'timeout',
        errorData
      );
    }

    // Check if error message contains Cloudflare 504 error page
    if (errorData && (errorData.includes('Gateway time-out') || errorData.includes('504'))) {
      throw new APIError(
        'AI response timeout. Server is processing heavy requests, please try again later.',
        504,
        'timeout',
        errorData
      );
    }

    throw new APIError(
      `API request failed: ${errorData}`,
      response.status,
      'server',
      errorData
    );
  }

  return response;
};

// Generate mock response for development
const generateMockResponse = async (firstName: string, surname: string): Promise<GenerateNameResponse> => {
  console.log('🚧 Development mode: Generating mock Chinese names...');
  
  const englishName = `${firstName} ${surname}`;
  
  // 延迟模拟API调用时间
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        suggestions: [
          {
            chineseName: "李德维",
            chineseSurname: "李",
            chineseGivenName: "德维",
            pinyin: "Lǐ Déwéi",
            englishName: englishName,
            surnameExplanation: "Li (李) is one of the most common surnames in China, originating from the plum tree. Historically, it was the imperial surname of the Tang Dynasty (618-907 CE) and represents wisdom and longevity.",
            givenNameExplanation: `Déwéi (德维) combines 'moral virtue' (德) with 'maintain/preserve' (维), creating a meaningful name for ${firstName} that suggests someone who upholds moral principles.`,
            culturalConnection: "This name embodies the traditional Chinese emphasis on moral character and ethical behavior, reflecting the Confucian value of personal cultivation and moral integrity.",
            additionalNotes: `Generated for ${firstName} ${surname}. This name would be particularly suitable for someone who values ethics, leadership, and personal development.`
          },
          {
            chineseName: "李天佑",
            chineseSurname: "李",
            chineseGivenName: "天佑",
            pinyin: "Lǐ Tiānyòu",
            englishName: englishName,
            surnameExplanation: "Li (李) represents the plum tree, symbolizing resilience and perseverance through harsh conditions, and is one of China's most prestigious family names.",
            givenNameExplanation: `Tiānyòu (天佑) means 'blessed by heaven', perfect for ${firstName}, combining 'heaven/sky' (天) with 'protection/blessing' (佑).`,
            culturalConnection: "This name reflects the traditional Chinese belief in celestial influence and divine protection, common in names expressing hope for a blessed life.",
            additionalNotes: `Customized for ${firstName} ${surname}. Perfect for someone whose parents hope for divine guidance and protection throughout life.`
          },
          {
            chineseName: "李明轩",
            chineseSurname: "李",
            chineseGivenName: "明轩",
            pinyin: "Lǐ Míngxuān",
            englishName: englishName,
            surnameExplanation: "Li (李) is deeply rooted in Chinese history and culture, representing nobility, wisdom, and the enduring strength of the plum blossom.",
            givenNameExplanation: `Míngxuān (明轩) combines 'bright/brilliant' (明) with 'high pavilion' (轩), suggesting ${firstName} has intelligence and elevated aspirations.`,
            culturalConnection: "This name embodies the scholarly tradition of ancient China, where educated individuals would study in elevated pavilions, symbolizing both wisdom and social status.",
            additionalNotes: `Tailored for ${firstName} ${surname}. Ideal for someone with academic ambitions or intellectual pursuits, suggesting brilliance and noble character.`
          }
        ]
      });
    }, 1500); // 1.5秒延迟模拟API响应时间
  });
};

// Generate Chinese Name
export const generateChineseName = async (
  firstName: string,
  surname: string,
  gender: 'boy' | 'girl' | 'any' = 'any'
): Promise<GenerateNameResponse> => {
  console.log('=== Starting Name Generation Request ===');
  console.log('Request Parameters:', { firstName, surname, gender });
  console.log('Environment:', isLocalDev ? 'Local Development' : 'Production');

  try {
    validateRequest({ firstName, surname });

    console.log('🌐 Making real API request...');

    // 统一的API调用逻辑（本地和生产环境都使用真实API）
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= CONFIG.RETRY_COUNT; attempt++) {
      if (attempt > 0) {
        console.log(`Retry attempt ${attempt}/${CONFIG.RETRY_COUNT}`);
        await delay(CONFIG.RETRY_DELAY);
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        try {
          controller.abort();
        } catch (e) {
          console.error('Failed to abort request:', e);
        }
      }, CONFIG.TIMEOUT);

      try {
        const response = await makeRequest(firstName, surname, gender, controller.signal);
        
        console.log('Response received:');
        console.log('Status:', response.status);
        console.log('Status text:', response.statusText);

        const data = await response.json();
        clearTimeout(timeoutId);

        if (!data.suggestions || !Array.isArray(data.suggestions)) {
          throw new APIError('Invalid API response: missing or invalid suggestions array');
        }

        return data as GenerateNameResponse;
      } catch (error) {
        lastError = error as Error;
        clearTimeout(timeoutId);

        // Check if it's a network error (connection refused, etc.)
        const isNetworkError = error instanceof TypeError && error.message.includes('Failed to fetch') ||
                              error instanceof TypeError && error.message.includes('ERR_CONNECTION_REFUSED') ||
                              (error as any)?.message?.includes('network') ||
                              (error as any)?.code === 'ECONNREFUSED';

        if (isNetworkError && import.meta.env.DEV) {
          console.log(`⚠️ Network error detected. Backend server may not be running.`);
          // Don't retry if it's a network error in dev mode - fallback to mock
          break;
        }

        // Don't retry on 504 timeout errors - they won't resolve with retry
        if (error instanceof APIError && error.statusCode === 504) {
          console.log(`504 Gateway Timeout detected. No retry will help.`);
          break;
        }

        if (error instanceof APIError && error.type === 'timeout') {
          console.log(`Attempt ${attempt + 1} timed out`);
          // Only retry once for timeout
          if (attempt >= CONFIG.RETRY_COUNT) {
            break;
          }
          continue;
        }
        
        if (error.name === 'AbortError') {
          console.log(`Attempt ${attempt + 1} was aborted`);
          if (attempt >= CONFIG.RETRY_COUNT) {
            break;
          }
          continue;
        }

        console.log(`Attempt ${attempt + 1} failed:`, error);
        
        if (attempt === CONFIG.RETRY_COUNT) {
          break;
        }
      }
    }

    // If we've exhausted all retries, fallback to mock data in development
    console.log('\n=== Name Generation Process Failed ===');
    console.log('Error Type:', lastError instanceof APIError ? lastError.type : 'unknown');
    console.log('Error Message:', lastError?.message || 'Unknown error');
    console.log('Full Error:', lastError);
    
    // In development, fallback to mock data if API server is not available
    const isNetworkError = lastError instanceof TypeError && lastError.message.includes('Failed to fetch') ||
                          lastError instanceof TypeError && lastError.message.includes('ERR_CONNECTION_REFUSED') ||
                          (lastError as any)?.message?.includes('network') ||
                          (lastError as any)?.code === 'ECONNREFUSED';
    
    if (import.meta.env.DEV && isNetworkError) {
      console.log('⚠️ Backend API server not available. Falling back to mock data...');
      console.log('💡 To use real API, run: npm run dev (starts both frontend and backend)');
      return await generateMockResponse(firstName, surname);
    }
    
    throw lastError || new APIError('Maximum retry attempts reached');
  } catch (error) {
    console.log('\n=== Name Generation Process Error ===');
    console.log('Error Type:', error instanceof APIError ? error.type : 'unknown');
    console.log('Error Message:', error instanceof Error ? error.message : String(error));
    console.log('Full Error:', error);
    throw error;
  }
};